// Spiderfy stazioni impilate: quando un click cade su ≥2 stazioni nello stesso
// punto (es. Comiso, Catania-Via Nicito), invece di aprire solo quella "sopra"
// le esplode a ventaglio con una gamba dal centro verso ogni stazione, tutte
// cliccabili singolarmente. Overlay DOM+SVG puro: NON tocca il layer GL né la
// logica nearest-anchor del click. Collassa al pan/zoom, Esc o click fuori.
// Un click su una gamba dispatcha lo `wsv2:station-click` singolo classico.
//
// Design "sistemato" (2026-07-08): niente più box callout a larghezza variabile
// impilati. Pin circolari UNIFORMI (solo il valore) distribuiti su un anello
// che si dimensiona così da non sovrapporsi MAI; scrim che attenua la mappa
// affollata sotto; nome della stazione come caption sul lato esterno del pin.

import { pickTextAndStroke } from '../layers/station-sprites.js';

const PIN_D = 38;     // diametro pin (px) — uniforme
const PIN_GAP = 16;   // spazio minimo tra pin adiacenti sull'anello
const MIN_R = 62;     // raggio minimo dell'anello

function esc(s) {
    return String(s ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}

function shortName(rec) {
    const s = rec.station || {};
    return s.localita || s.location || s.comune || s.city || s.name || s.nome || 'Stazione';
}

let overlay = null;
let scrim = null;
let legsSvg = null;
let openMap = null;      // map se aperto, null se chiuso
let outsideHandler = null;

function ensureOverlay(map) {
    if (overlay) return overlay;
    overlay = document.createElement('div');
    overlay.className = 'wsv2-spider';
    overlay.innerHTML =
        '<div class="wsv2-spider__scrim"></div>' +
        '<svg class="wsv2-spider__legs" aria-hidden="true"></svg>';
    scrim = overlay.querySelector('.wsv2-spider__scrim');
    legsSvg = overlay.querySelector('.wsv2-spider__legs');
    scrim.addEventListener('pointerdown', closeSpider);
    map.getContainer().appendChild(overlay);
    return overlay;
}

export function closeSpider() {
    if (!openMap) return;
    const map = openMap;
    openMap = null;
    if (overlay) {
        overlay.querySelectorAll('.wsv2-spider__pin').forEach(n => n.remove());
        if (legsSvg) legsSvg.replaceChildren();
        overlay.classList.remove('is-open');
    }
    map.off('movestart', closeSpider);
    map.off('zoomstart', closeSpider);
    if (outsideHandler) { document.removeEventListener('pointerdown', outsideHandler, true); outsideHandler = null; }
}

// Geometria del ventaglio: anello unico dimensionato in modo che i pin (di
// diametro fisso) NON si tocchino mai. Il raggio cresce col numero di stazioni
// così che la circonferenza contenga sempre n·(PIN_D+PIN_GAP). Ritorna array di
// offset px {dx,dy} dal centro + l'angolo (per piazzare la caption verso fuori).
function fanOffsets(n) {
    const chord = PIN_D + PIN_GAP;                 // spazio richiesto per pin
    const R = Math.max(MIN_R, (n * chord) / (2 * Math.PI));
    const out = [];
    const start = -Math.PI / 2;                     // primo pin in alto
    for (let i = 0; i < n; i++) {
        const a = start + (i * 2 * Math.PI) / n;
        out.push({ dx: R * Math.cos(a), dy: R * Math.sin(a), a });
    }
    return out;
}

function openSpider(map, cluster) {
    closeSpider();
    ensureOverlay(map);
    openMap = map;
    overlay.classList.add('is-open');

    // Centro = baricentro proiettato delle stazioni impilate (sono ~coincidenti).
    let cx = 0, cy = 0;
    for (const r of cluster) { const p = map.project([r.lon, r.lat]); cx += p.x; cy += p.y; }
    cx /= cluster.length; cy /= cluster.length;

    // centra il "foro" trasparente dello scrim sul cluster
    if (scrim) { scrim.style.setProperty('--sx', cx + 'px'); scrim.style.setProperty('--sy', cy + 'px'); }

    const offs = fanOffsets(cluster.length);

    // pallino-perno al centro
    const hub = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
    hub.setAttribute('cx', cx); hub.setAttribute('cy', cy); hub.setAttribute('r', '4');
    hub.setAttribute('class', 'wsv2-spider__hub');
    legsSvg.appendChild(hub);

    cluster.forEach((rec, i) => {
        const ex = cx + offs[i].dx, ey = cy + offs[i].dy;
        // gamba dal perno al pin
        const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
        line.setAttribute('x1', cx); line.setAttribute('y1', cy);
        line.setAttribute('x2', ex); line.setAttribute('y2', ey);
        line.setAttribute('class', 'wsv2-spider__leg');
        legsSvg.appendChild(line);

        // pin circolare uniforme: solo il valore, colore del parametro
        const pin = document.createElement('button');
        pin.type = 'button';
        pin.className = 'wsv2-spider__pin';
        pin.style.left = ex + 'px';
        pin.style.top = ey + 'px';
        const bg = rec.color || '#0079b8';
        pin.style.setProperty('--c', bg);
        try { pin.style.color = pickTextAndStroke(bg).fill; } catch (e) {}
        pin.style.transitionDelay = (i * 22) + 'ms';

        // caption nome verso il lato ESTERNO del pin (sopra se in alto, sotto se
        // in basso) → resta leggibile e non copre l'anello.
        const capSide = offs[i].dy < -6 ? 'up' : 'down';
        pin.innerHTML =
            `<span class="wsv2-spider__val">${esc(rec.text ?? '')}</span>` +
            `<span class="wsv2-spider__cap wsv2-spider__cap--${capSide}">${esc(shortName(rec))}</span>`;

        pin.setAttribute('title', shortName(rec));
        pin.addEventListener('click', (ev) => {
            ev.stopPropagation();
            closeSpider();
            document.dispatchEvent(new CustomEvent('wsv2:station-click', { detail: { rec, cluster: [rec] } }));
        });
        overlay.appendChild(pin);
        // "pop" dal centro verso la posizione finale
        requestAnimationFrame(() => pin.classList.add('is-out'));
    });

    // chiusure
    map.on('movestart', closeSpider);
    map.on('zoomstart', closeSpider);
    setTimeout(() => {
        outsideHandler = (e) => { if (!e.target.closest('.wsv2-spider__pin')) closeSpider(); };
        document.addEventListener('pointerdown', outsideHandler, true);
    }, 0);
}

export function initSpiderfy(map) {
    document.addEventListener('wsv2:station-cluster', (e) => {
        const cluster = e.detail?.cluster || [];
        if (cluster.length < 2) return;
        openSpider(map, cluster);
    });
    // se si apre una scheda stazione (da ricerca, deep-link, gamba) chiudi il ragno
    document.addEventListener('wsv2:station-click', closeSpider);
    document.addEventListener('keydown', (ev) => { if (ev.key === 'Escape') closeSpider(); });
}
